import React from "react";
import { Box, IconButton, Link, Tooltip, useTheme } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";

export const filenameFromUrl = (url: string, fallback: string): string => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const name = path.split("/").filter(Boolean).pop();
    if (name && name.includes(".")) return decodeURIComponent(name);
  } catch {
    // keep fallback
  }
  return fallback;
};

const downloadFromUrl = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Failed to download image");
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error("Failed to download image as blob:", error);
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (fallbackError) {
      console.error("Failed to download image:", fallbackError);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }
};

export const purchaseRequestDetailsPath = (
  prId: number,
  orderId?: number | null,
): string => {
  const params = new URLSearchParams({ prId: String(prId) });
  if (orderId != null && Number.isFinite(orderId) && orderId > 0) {
    params.set("projectId", String(orderId));
  }
  return `/purchase/requests/details?${params.toString()}`;
};

export const IdLink: React.FC<{
  href: string;
  label: string;
  fontSize?: string;
  onNavigate?: () => void;
}> = ({ href, label, fontSize = "0.85rem", onNavigate }) => {
  const theme = useTheme();
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      onClick={(event) => {
        event.stopPropagation();
        try {
          onNavigate?.();
        } catch {
          // never block navigation
        }
      }}
      sx={{
        color: theme.palette.customColors.purple,
        fontWeight: 500,
        fontSize,
      }}
    >
      {label}
    </Link>
  );
};

export const ImagePreviewFrame: React.FC<{
  src: string;
  alt: string;
  filename: string;
}> = ({ src, alt, filename }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;

  const openInNewTab = () => {
    window.open(src, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 180,
        flexShrink: 0,
        bgcolor: colors.veryLightGray,
        overflow: "hidden",
        "&:hover .image-actions": { opacity: 1 },
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        onClick={openInNewTab}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          objectPosition: "center",
          display: "block",
          cursor: "pointer",
        }}
      />
      <Box
        className="image-actions"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 0.5,
          opacity: { xs: 1, sm: 0 },
          transition: "opacity 0.15s ease",
        }}
      >
        <Tooltip title="Open in new tab">
          <IconButton
            size="small"
            onClick={openInNewTab}
            sx={{
              width: 28,
              height: 28,
              bgcolor: "rgba(255,255,255,0.92)",
              color: colors.navyText,
              "&:hover": { bgcolor: colors.white },
            }}
          >
            <OpenInNewIcon sx={{ fontSize: "0.95rem" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton
            size="small"
            onClick={() => {
              void downloadFromUrl(src, filename);
            }}
            sx={{
              width: 28,
              height: 28,
              bgcolor: "rgba(255,255,255,0.92)",
              color: colors.navyText,
              "&:hover": { bgcolor: colors.white },
            }}
          >
            <DownloadIcon sx={{ fontSize: "0.95rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
